import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import VirtualAccountBalance from "./components/VirtualAccountBalance";
import ActiveTradesTable from "./components/ActiveTradesTable";
import TradingSimulationControls from "./components/TradingSimulationControls";
import PerformanceAnalytics from "./components/PerformanceAnalytics";
import BotAssignmentPanel from "./components/BotAssignmentPanel";
import ActiveBots from "./components/ActiveBots";
import { ChevronDown } from "lucide-react";
import apiUtils from "utils/apiutils";

const PaperTrading = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [simulationStatus, setSimulationStatus] = useState("stopped");
  const [selectedBots, setSelectedBots] = useState(["bot1", "bot2"]);
  const [assignedBots, setAssignedBots] = useState([
    {
      id: "bot1",
      name: "Gold Rush Scalper",
      strategy: "Scalping",
      exchange: "Binance",
      status: "active",
      performance: { roi: 15.2, trades: 145, winRate: 72 },
      risk: "Medium",
    },
    {
      id: "bot2",
      name: "Trend Follower Pro",
      strategy: "Trend Following",
      exchange: "KuCoin",
      status: "active",
      performance: { roi: 23.8, trades: 89, winRate: 68 },
      risk: "High",
    },
  ]);

  const [accountData, setAccountData] = useState({
    startingCapital: 10000,
    currentValue: 11100,
    unrealizedPnL: 850,
    totalTrades: 32,
    winRate: 68.5,
  });

  const [activeTrades, setActiveTrades] = useState([
    {
      id: "trade1",
      pair: "BTC/USDT",
      side: "LONG",
      size: "0.25 BTC",
      entryPrice: 43250,
      currentPrice: 44180,
      pnl: 232.5,
      timestamp: new Date(Date.now() - 3600000),
    },
    {
      id: "trade2",
      pair: "ETH/USDT",
      side: "SHORT",
      size: "5.0 ETH",
      entryPrice: 2580,
      currentPrice: 2545,
      pnl: 175.0,
      timestamp: new Date(Date.now() - 1800000),
    },
    {
      id: "trade3",
      pair: "ADA/USDT",
      side: "LONG",
      size: "2500 ADA",
      entryPrice: 0.485,
      currentPrice: 0.478,
      pnl: -17.5,
      timestamp: new Date(Date.now() - 900000),
    },
    {
      id: "trade4",
      pair: "SOL/USDT",
      side: "LONG",
      size: "15 SOL",
      entryPrice: 98.5,
      currentPrice: 102.3,
      pnl: 57.0,
      timestamp: new Date(Date.now() - 600000),
    },
  ]);

  const [showExportModal, setShowExportModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    // Simulate real-time price updates
    const interval = setInterval(() => {
      if (simulationStatus === "running") {
        setActiveTrades((prevTrades) =>
          prevTrades?.map((trade) => ({
            ...trade,
            currentPrice:
              trade?.currentPrice +
              (Math.random() - 0.5) * (trade?.currentPrice * 0.001),
            pnl: trade?.pnl + (Math.random() - 0.5) * 10,
          }))
        );

        setAccountData((prev) => ({
          ...prev,
          currentValue: prev?.currentValue + (Math.random() - 0.5) * 50,
          unrealizedPnL: prev?.unrealizedPnL + (Math.random() - 0.5) * 20,
        }));
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [simulationStatus]);

  const handleStartSimulation = () => {
    if (selectedBots?.length === 0) {
      alert("Please select at least one bot to start the simulation.");
      return;
    }
    setSimulationStatus("running");
  };

  const handleStopSimulation = () => {
    setSimulationStatus("stopped");
  };

  const handleResetSimulation = () => {
    setSimulationStatus("stopped");
    setAccountData({
      startingCapital: 10000,
      currentValue: 10000,
      unrealizedPnL: 0,
      totalTrades: 0,
      winRate: 0,
    });
    setActiveTrades([]);
  };

  const handleResetBalance = () => {
    const confirmReset = window.confirm(
      "Are you sure you want to reset the virtual balance? This will close all positions and reset your account to the starting capital."
    );
    if (confirmReset) {
      handleResetSimulation();
    }
  };

  const handleCloseTrade = (tradeId) => {
    const confirmClose = window.confirm(
      "Are you sure you want to close this position?"
    );
    if (confirmClose) {
      setActiveTrades((prev) => prev?.filter((trade) => trade?.id !== tradeId));
    }
  };

  const handleBotSelection = (botIds) => {
    setSelectedBots(botIds);
  };

  const handleBotAssignment = (bot) => {
    setAssignedBots((prev) => [...prev, bot]);
  };

  const handleBotRemoval = (botId) => {
    setAssignedBots((prev) => prev?.filter((bot) => bot?.id !== botId));
    setSelectedBots((prev) => prev?.filter((id) => id !== botId));
  };

  const handleExportResults = () => {
    setShowExportModal(true);
  };

  const handleConvertToLive = () => {
    setShowConvertModal(true);
  };

  const exportFormats = [
    {
      id: "pdf",
      label: "PDF Report",
      icon: "FileText",
      description: "Comprehensive trading report",
    },
    {
      id: "csv",
      label: "CSV Data",
      icon: "Table",
      description: "Raw trade data for analysis",
    },
    {
      id: "excel",
      label: "Excel Workbook",
      icon: "Sheet",
      description: "Formatted spreadsheet with charts",
    },
  ];

  const performanceData = {
    totalReturn: 11.0,
    sharpeRatio: 1.85,
    maxDrawdown: -3.2,
    winRate: 68.5,
  };

  //
  const [selectedBot, setSelectedBot] = useState("");
  const [tradeMode, setTradeMode] = useState("tp_sl");
  const [open, setOpen] = useState(false);

  const handleSelect = (bot) => {
    setSelectedBot(bot);
    setOpen(false); // close dropdown after selecting
  };
  const [bots, setBots] = useState([]);
  const botNames = bots.map((bot) => bot.Bot_Name); // Assuming the bot names are under the "Bot_Name" key

  const fetchBots = async () => {
    const payloads = {
      page_no: 1,
      page_size: 2000,
      strategy_name: tradeMode?.toUpperCase(),
      trade_type: "PAPER",
    };

    try {
      const resp = await apiUtils.post("/bot/view_bot", payloads, {
        showToast: false,
      });
      if (resp?.Success) {
        setBots(resp.Success);
      } else {
        setBots([]);
      }
    } catch (e) {
      console.error("Error fetching bots:", e);
      setBots([]);
    } finally {
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
              <Icon name="TestTube" size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Paper Trading
              </h1>
              <p className="text-muted-foreground">
                Risk-free strategy testing with virtual balance simulation
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-6">
            {/* Bot Dropdown */}
            <div className="relative w-44">
              {/* Trigger Button */}
              <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex justify-between items-center w-full text-primary bg-secondary border border-primary rounded-lg px-4 py-2 text-sm font-medium hover:border-primary/80 focus:outline-none transition-all"
              >
                {selectedBot || "Select Bot Name"}
                <ChevronDown
                  className={`w-4 h-4 ml-2 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown Options */}
              {open && (
                <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                  {botNames.map((bot) => (
                    <div
                      key={bot}
                      onClick={() => handleSelect(bot)}
                      className={`px-4 py-2 text-sm cursor-pointer rounded-md transition-colors ${
                        selectedBot === bot
                          ? "bg-primary text-white"
                          : "bg-secondary hover:bg-primary/10 hover:text-primary"
                      }`}
                    >
                      {bot}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Radio Buttons */}
           <div className="flex items-center bg-secondary rounded-full p-1 w-full sm:w-auto">
  <label
    className={`cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all ${
      tradeMode === "tp_sl"
        ? "bg-primary text-secondary"
        : "text-gray-600 hover:bg-secondary"
    }`}
  >
    <input
      type="radio"
      name="tradeMode"
      value="tp_sl"
      checked={tradeMode === "tp_sl"}
      onChange={(e) => setTradeMode(e.target.value)}
      className="hidden"
    />
    TP / SL
  </label>

  <label
    className={`cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all ${
      tradeMode === "liquidation"
        ? "bg-primary text-secondary"
        : "text-gray-600 hover:bg-secondary"
    }`}
  >
    <input
      type="radio"
      name="tradeMode"
      value="liquidation"
      checked={tradeMode === "liquidation"}
      onChange={(e) => setTradeMode(e.target.value)}
      className="hidden"
    />
    Liquidation
  </label>
</div>

          </div>
        </div>

        {/* Virtual Account Balance */}
        <VirtualAccountBalance
          accountData={accountData}
          onResetBalance={handleResetBalance}
        />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Left Column - Controls and Bot Assignment */}
          <div className="space-y-6">
            <TradingSimulationControls
              simulationStatus={simulationStatus}
              onStartSimulation={handleStartSimulation}
              onStopSimulation={handleStopSimulation}
              onResetSimulation={handleResetSimulation}
              selectedBots={selectedBots}
              onBotSelection={handleBotSelection}
            />

            <BotAssignmentPanel
              assignedBots={assignedBots}
              onBotAssignment={handleBotAssignment}
              onBotRemoval={handleBotRemoval}
            />
          </div>

          {/* Right Column - Trades and Analytics */}
          <div className="xl:col-span-2 space-y-6">
            {/* <ActiveTradesTable
              trades={activeTrades}
              onCloseTrade={handleCloseTrade}
            /> */}
            <ActiveBots strategyName={tradeMode} botname={selectedBot} />

            <PerformanceAnalytics
              performanceData={performanceData}
              onExportResults={handleExportResults}
            />
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-2xl p-6 trading-shadow">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Quick Actions
              </h3>
              <p className="text-muted-foreground">
                Navigate to related sections for comprehensive bot management
              </p>
            </div>
            <div className="flex flex-col lg:flex-row items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/bot-marketplace")}
                iconName="Store"
                iconPosition="left"
              >
                Bot Marketplace
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/bot-creation-wizard")}
                iconName="Wrench"
                iconPosition="left"
              >
                Create Bot
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/backtesting-engine")}
                iconName="BarChart3"
                iconPosition="left"
              >
                Backtesting
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard/bot-analytics")}
                iconName="Activity"
                iconPosition="left"
              >
                Analytics
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Export Results Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full mx-4 trading-shadow">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Download" size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Export Paper Trading Results
                </h3>
                <p className="text-sm text-muted-foreground">
                  Choose your preferred export format
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {exportFormats?.map((format) => (
                <button
                  key={format?.id}
                  onClick={() => {
                    console.log(`Exporting as ${format?.label}`);
                    setShowExportModal(false);
                  }}
                  className="w-full p-4 bg-muted/30 hover:bg-muted/50 rounded-xl text-left trading-transition"
                >
                  <div className="flex items-center space-x-3">
                    <Icon
                      name={format?.icon}
                      size={20}
                      className="text-primary"
                    />
                    <div>
                      <p className="font-medium text-foreground">
                        {format?.label}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format?.description}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowExportModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Convert to Live Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full mx-4 trading-shadow">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-success/10 rounded-xl flex items-center justify-center">
                <Icon name="Zap" size={20} className="text-success" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Convert to Live Trading
                </h3>
                <p className="text-sm text-muted-foreground">
                  Deploy your successful strategy
                </p>
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-xl p-4 mb-6">
              <div className="flex items-start space-x-3">
                <Icon
                  name="AlertTriangle"
                  size={20}
                  className="text-warning mt-0.5"
                />
                <div>
                  <p className="font-medium text-warning mb-1">
                    Important Notice
                  </p>
                  <p className="text-sm text-foreground">
                    Converting to live trading will use real funds. Ensure you
                    have:
                  </p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Connected exchange API keys</li>
                    <li>• Sufficient account balance</li>
                    <li>• Reviewed risk management settings</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowConvertModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  navigate("/bot-details");
                  setShowConvertModal(false);
                }}
                className="flex-1"
              >
                Continue to Setup
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaperTrading;
